import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Disclaimer, Spinner } from "@/components/shared";
import { ArrowLeft, ExternalLink, Bookmark, BookmarkCheck, Plus, FileText, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const JURIS = { ON: "Ontario", CA: "Federal (Canada)" };

export default function ProgramDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPaid } = useAuth();
  const [p, setP] = useState(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [prog, savedList] = await Promise.all([
          api.get(`/programs/${id}`).then((r) => r.data),
          api.get("/saved-programs").then((r) => r.data).catch(() => []),
        ]);
        setP(prog);
        setSaved(savedList.some((s) => s.id === id));
      } catch (e) { toast.error("Program not found."); navigate("/app/funding"); }
      finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const toggleSave = async () => {
    const { data } = await api.post(`/programs/${id}/save`);
    setSaved(data.saved);
    toast.success(data.saved ? "Saved to review." : "Removed from saved.");
  };

  const createTask = async () => {
    try {
      await api.post("/tasks", { title: `Review: ${p.name}`, description: p.plain_language_summary, category: "funding_application", priority: "medium", linked_program_id: p.id });
      toast.success("Task created.");
      navigate("/app/tasks");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <Spinner />;
  if (!p) return null;

  const date = p.last_reviewed_at ? new Date(p.last_reviewed_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—";

  return (
    <div className="max-w-3xl">
      <button onClick={() => navigate("/app/funding")} className="inline-flex items-center gap-1.5 text-kid-muted hover:text-kid-teal font-poppins text-sm mb-5" data-testid="program-back">
        <ArrowLeft size={16} /> Back to Funding Finder
      </button>

      <div className="kid-card p-7 sm:p-9" data-testid="program-detail">
        <div className="flex gap-2 mb-4">
          <span className="text-[.68rem] font-poppins font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-kid-skytint text-kid-teal">{JURIS[p.jurisdiction] || p.jurisdiction}</span>
          <span className="text-[.68rem] font-poppins font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-kid-mist text-kid-muted">{p.category.replace(/_/g, " ")}</span>
        </div>
        <h1 className="text-3xl font-extrabold text-kid-navy">{p.name}</h1>
        <p className="text-kid-ink/80 mt-3 text-lg">{p.plain_language_summary}</p>

        <div className="flex flex-wrap gap-3 mt-6">
          <button onClick={toggleSave} className="kid-btn kid-btn-ghost h-11 px-5" data-testid="program-detail-save">
            {saved ? <BookmarkCheck size={16} className="text-kid-teal" /> : <Bookmark size={16} />} {saved ? "Saved" : "Save to review"}
          </button>
          {isPaid && (
            <button onClick={createTask} className="kid-btn kid-btn-ghost h-11 px-5" data-testid="program-detail-task"><Plus size={16} /> Create task</button>
          )}
          {p.application_url && (
            <a href={p.application_url} target="_blank" rel="noopener noreferrer" className="kid-btn kid-btn-primary h-11 px-5" data-testid="program-application-link">
              Open application page <ExternalLink size={15} />
            </a>
          )}
        </div>

        {p.who_may_review && (
          <div className="mt-8">
            <h3 className="font-bold text-kid-navy mb-1.5">Who may want to review this</h3>
            <p className="text-kid-muted">{p.who_may_review}</p>
          </div>
        )}

        {p.common_documents_needed?.length > 0 && (
          <div className="mt-6">
            <h3 className="font-bold text-kid-navy mb-2">Common documents needed</h3>
            <ul className="space-y-1.5">
              {p.common_documents_needed.map((d, i) => (
                <li key={i} className="flex items-center gap-2 text-kid-ink/80"><FileText size={16} className="text-kid-teal" /> {d}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-5 mt-7 pt-6 border-t border-kid-line">
          {p.official_source_url && (
            <a href={p.official_source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-kid-teal font-poppins font-semibold text-sm" data-testid="program-source-link">
              Open official source <ExternalLink size={15} />
            </a>
          )}
          <span className="inline-flex items-center gap-1.5 text-kid-muted text-sm"><CalendarClock size={15} /> Last reviewed {date}</span>
        </div>

        <div className="mt-6">
          <Disclaimer>{p.disclaimer}</Disclaimer>
        </div>
      </div>
    </div>
  );
}
